import { action, flow, makeObservable, observable } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { DrawActionsService } from '../services/drawActionsService';

export const HTTP_ERROR_CODE_FAILED_DEPENDENCY = 424;

const INSURANCE_DRAW = 'Insurance Draw';
const REGULAR_DRAW = 'Draw';
export const DRAW_INSURANCE_TYPE = "insurance";
export const DRAW_REGULAR_TYPE = "regular";
export const DRAWS_REVISION_STAGE = "SOW Revision";
export const SELF_INSPECTION = "Self Inspection"
export const SELF_INSPECTION_MOBILE = 'Self Inspection Mobile';

export class DrawActionsStore {
  globalStore: GlobalStore;
  private drawActionsService: DrawActionsService;

  //loan actions
  isAddDrawDialogOpen: boolean;
  freezeFundingStatus: boolean;
  isFreezeFundingDialogOpen: boolean;

  //draw actions
  isApproveSOWsDialogOpen: boolean;
  isStartInspectionProcessDialogOpen: boolean;
  isApproveInspectionDialogOpen: boolean;
  isRequestBankInformationDialogOpen: boolean;
  isSendWireDialogOpen: boolean
  isApproveWireDialogOpen: boolean;
  isResolveDrawDialogOpen: boolean;
  isRejectDrawDialogOpen: boolean;
  isSetDrawStageDialogOpen: boolean;
  isLinkTransactionToDrawDialogOpen: boolean;
  isMoveSnapDrawsPhotosDialogOpen: boolean;
  isApproveRevisionDialogOpen: boolean;
  isRejectRevisionDialogOpen: boolean;

  inspectionCompany: string;
  docusignVerification: string;
  rejectReason: string;
  newStage: string;
  linkTransactionOption: number;
  transactionList: any;
  inspectionOpen: boolean;
  truepicOption: any;
  truepicEvent: any;
  truepicEventList: any;
  isTruepicConfirmDialogOpen: boolean;

  constructor(globalStore) {
    this.globalStore = globalStore;
    this.drawActionsService = new DrawActionsService();
    this.setDefaults();

    makeObservable(this, {
      setDefaults: action,
      isAddDrawDialogOpen: observable,
      setIsAddDrawDialogOpen: action,
      saveDraw: flow,
      freezeFundingStatus: observable,
      isFreezeFundingDialogOpen: observable,
      setIsFreezeFundingDialogOpen: action,
      isSendWireDialogOpen: observable,
      setIsSendWireDialogOpen: action,
      getFreezeFundingStatus: flow,
      switchFreezeFunding: flow,
      isApproveSOWsDialogOpen: observable,
      setIsApproveSOWsDialogOpen: action,
      approveSOWs: flow,
      isStartInspectionProcessDialogOpen: observable,
      setIsStartInspectionProcessDialogOpen: action,
      sendDrawInspectionEmail: flow,
      isApproveInspectionDialogOpen: observable,
      setIsApproveInspectionDialogOpen: action,
      approveInspections: flow,
      inspectionCompany: observable,
      setInspectionCompany: action,
      docusignVerification: observable,
      setDocusignVerification: action,
      isRequestBankInformationDialogOpen: observable,
      setIsRequestBankInformationDialogOpen: action,
      sendDocusign: flow,
      isApproveWireDialogOpen: observable,
      setIsApproveWireDialogOpen: action,
      approveWire: flow,
      isResolveDrawDialogOpen: observable,
      setIsResolveDrawDialogOpen: action,
      resolveDraw: flow,
      isRejectDrawDialogOpen: observable,
      setIsRejectDrawDialogOpen: action,
      sendRejectionReason: flow,
      rejectReason: observable,
      setRejectReason: action,
      isSetDrawStageDialogOpen: observable,
      setIsSetDrawStageDialogOpen: action,
      updateDrawStage: flow,
      newStage: observable,
      setNewStage: action,
      openLinkTransactionToDrawDialog: flow,
      isLinkTransactionToDrawDialogOpen: observable,
      setIsLinkTransactionToDrawDialogOpen: action,
      getTransactionList: flow,
      linkTransactionId: flow,
      transactionList: observable,
      linkTransactionOption: observable,
      setLinkTransactionOption: action,
      openMoveSnapDrawsPhotosDialog: flow,
      isMoveSnapDrawsPhotosDialogOpen: observable,
      setIsMoveSnapDrawsPhotosDialogOpen: action,
      getSnapDrawsEvents: flow,
      getIfInspectionOpen: flow,
      inspectionOpen: observable,
      truepicOption: observable,
      setTruepicOption: action,
      truepicEventList: observable,
      truepicEvent: observable,
      isTruepicConfirmDialogOpen: observable,
      setIsTruepicConfirmDialogOpen: action,
      setTruepicEvent: action,
      moveTruepicPhotos: action,
      movePictures: flow,
      isApproveRevisionDialogOpen: observable,
      setIsApproveRevisionDialogOpen: action,
      approveRevision: flow,
      isRejectRevisionDialogOpen: observable,
      setIsRejectRevisionDialogOpen: action,
    });
  }

  setDefaults() {
    this.inspectionCompany = '';
    this.rejectReason = '';
    this.newStage = '';
    this.linkTransactionOption = null;
    this.transactionList = [];
    this.truepicEvent = null;
    this.truepicEventList = [];
    this.inspectionOpen = false;
    this.truepicOption = null;
    this.isTruepicConfirmDialogOpen = false;
    this.docusignVerification = 'withIdVerification';
    this.isRejectRevisionDialogOpen = false;
  }

  setIsAddDrawDialogOpen(isAddDrawDialogOpen: boolean) {
    this.isAddDrawDialogOpen = isAddDrawDialogOpen;
  }

  setIsFreezeFundingDialogOpen(isFreezeFundingDialogOpen: boolean) {
    this.isFreezeFundingDialogOpen = isFreezeFundingDialogOpen;
  }

  setIsSendWireDialogOpen(isSendWireDialogOpen: boolean) {
    this.isSendWireDialogOpen = isSendWireDialogOpen;
  }

  setIsApproveSOWsDialogOpen(isApproveSOWsDialogOpen: boolean) {
    this.isApproveSOWsDialogOpen = isApproveSOWsDialogOpen;
  }

  setIsStartInspectionProcessDialogOpen(isStartInspectionProcessDialogOpen: boolean) {
    this.isStartInspectionProcessDialogOpen = isStartInspectionProcessDialogOpen;
  }

  setIsApproveInspectionDialogOpen(isApproveInspectionDialogOpen: boolean) {
    this.isApproveInspectionDialogOpen = isApproveInspectionDialogOpen;
  }

  setInspectionCompany(inspectionCompany) {
    this.inspectionCompany = inspectionCompany;
  }

  setDocusignVerification(docusignVerification) {
    this.docusignVerification = docusignVerification;
  }

  setIsRequestBankInformationDialogOpen(isRequestBankInformationDialogOpen: boolean) {
    this.isRequestBankInformationDialogOpen = isRequestBankInformationDialogOpen;
  }

  setIsApproveWireDialogOpen(isApproveWireDialogOpen: boolean) {
    this.isApproveWireDialogOpen = isApproveWireDialogOpen;
  }

  setIsResolveDrawDialogOpen(isResolveDrawDialogOpen: boolean) {
    this.isResolveDrawDialogOpen = isResolveDrawDialogOpen;
  }

  setIsRejectDrawDialogOpen(isRejectDrawDialogOpen: boolean) {
    this.isRejectDrawDialogOpen = isRejectDrawDialogOpen;
  }

  setRejectReason(rejectReason) {
    this.rejectReason = rejectReason;
  }

  setIsSetDrawStageDialogOpen(isSetDrawStageDialogOpen: boolean) {
    this.isSetDrawStageDialogOpen = isSetDrawStageDialogOpen;
  }

  setNewStage(newStage) {
    this.newStage = newStage;
  }

  setIsLinkTransactionToDrawDialogOpen(isLinkTransactionToDrawDialogOpen: boolean) {
    this.isLinkTransactionToDrawDialogOpen = isLinkTransactionToDrawDialogOpen;
  }

  setLinkTransactionOption(linkTransactionOption) {
    this.linkTransactionOption = linkTransactionOption;
  }

  setIsMoveSnapDrawsPhotosDialogOpen(isMoveSnapDrawsPhotosDialogOpen: boolean) {
    this.isMoveSnapDrawsPhotosDialogOpen = isMoveSnapDrawsPhotosDialogOpen;
  }

  setIsTruepicConfirmDialogOpen(isTruepicConfirmDialogOpen) {
    this.isTruepicConfirmDialogOpen = isTruepicConfirmDialogOpen;
  }

  setTruepicOption(truepicOption) {
    this.truepicOption = truepicOption;
  }

  setTruepicEvent(truepicEvent) {
    this.truepicEvent = truepicEvent;
  }

  *saveDraw(data) {
    try {
      const response = yield this.drawActionsService.createDraw(data);
      const result = JSON.parse(response?.data?.data);
      if (result?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Draw created successfully.',
        });
      }
      else {
        this.globalStore.notificationStore.showErrorNotification({
          message: result?.responseMessage,
        });
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while creating Draw.',
      });
    }
    this.setIsAddDrawDialogOpen(false);
  }

  *getFreezeFundingStatus(loanId: string) {
    try {
      const response = yield this.drawActionsService.getFreezeFundingStatus(loanId);
      this.freezeFundingStatus = JSON.parse(response?.data?.data)?.status;
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching freeze funding status.',
      });
    }
  }

  *switchFreezeFunding(loanId: string) {
    try {
      yield this.drawActionsService.switchFreezeFunding(loanId);
      this.freezeFundingStatus = !this.freezeFundingStatus;
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Freeze Funding status updated.',
      });
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while trying to update the freeze funding status, please reach out to the tech team.',
      });
    }
    this.setIsFreezeFundingDialogOpen(false);
  }

  *approveSOWs(drawId, afterActionExecuted) {
    try {
      const response = yield this.drawActionsService.approveSOW(drawId);
      const result = JSON.parse(response?.data?.data);
      if (result?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'SOW(s) Approved successfully.',
        });
        afterActionExecuted && afterActionExecuted();
      }
      else {
        this.globalStore.notificationStore.showErrorNotification({
          message: result?.responseMessage,
        });
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while trying to approve the SOWs, please reach out to the tech team.',
      });
    }
    this.setIsApproveSOWsDialogOpen(false);
  }

  *sendDrawInspectionEmail(drawId, afterActionExecuted) {
    try {
      const response = yield this.drawActionsService.orderInspection(drawId, this.inspectionCompany);
      const result = JSON.parse(response?.data?.data);
      if (result?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: this.inspectionCompany === SELF_INSPECTION ? "Email sent successfully." : "We've successfully recorded your selection.",
        });
        afterActionExecuted && afterActionExecuted();
      }
      else {
        this.globalStore.notificationStore.showErrorNotification({
          message: result?.responseMessage,
        });
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Unable to complete the request, please contact the tech team.',
      });
    }
    this.setIsStartInspectionProcessDialogOpen(false);
  }

  *approveInspections(drawId, afterActionExecuted) {
    try {
      const response = yield this.drawActionsService.approveInspections(drawId);
      const result = JSON.parse(response?.data?.data);
      if (result?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Inspection approved successfully.',
        });
        afterActionExecuted && afterActionExecuted();
      }
      else {
        this.globalStore.notificationStore.showErrorNotification({
          message: result?.responseMessage,
        });
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while trying to approve the inspections, please reach out to the tech team.',
      });
    }
    this.setIsApproveInspectionDialogOpen(false);
    this.inspectionCompany = '';
  }

  *sendDocusign(drawId, afterActionExecuted) {
    try {
      const response = yield this.drawActionsService.verifyWireDetails(drawId, this.docusignVerification == 'withIdVerification');
      const result = JSON.parse(response?.data?.data);
      if (result?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Request made successfully.',
        });
        afterActionExecuted && afterActionExecuted();
      }
      else {
        this.globalStore.notificationStore.showErrorNotification({
          message: result?.responseMessage,
        });
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There it was an error while trying to verify wire details, please reach out to the tech team.',
      });
    }
    this.setIsRequestBankInformationDialogOpen(false);
  }

  *approveWire(drawId, afterActionExecuted) {
    try {
      const response = yield this.drawActionsService.approveWire(drawId);
      const result = JSON.parse(response?.data?.data);
      if (result?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Draw Wire approved successfully.',
        });
        afterActionExecuted && afterActionExecuted();
      }
      else {
        this.globalStore.notificationStore.showErrorNotification({
          message: result,
        });
      }
    } catch (err) {
      console.log(err);
      if (err.response.status === HTTP_ERROR_CODE_FAILED_DEPENDENCY) {
        this.globalStore.notificationStore.showErrorNotification({
          message: err.response?.data,
        });
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'There was an error while trying to approve the Draw Wire, please reach out to the tech team.',
        });
      }
    }
    this.setIsApproveWireDialogOpen(false);
  }

  *resolveDraw(drawId, afterActionExecuted) {
    try {
      const response = yield this.drawActionsService.resolveDraw(drawId);
      const result = JSON.parse(response?.data?.data);
      if (result?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Draw successfully resolved.',
        });
        afterActionExecuted && afterActionExecuted();
      }
      else {
        this.globalStore.notificationStore.showErrorNotification({
          message: result?.responseMessage,
        });
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while trying to resolve the draw, please reach out to the tech team.',
      });
    }
    this.setIsResolveDrawDialogOpen(false);
  }

  *sendRejectionReason(drawId, afterActionExecuted) {
    try {
      if (this.rejectReason) {
        const response = yield this.drawActionsService.rejectSOWrevision(drawId, this.rejectReason);
        const result = JSON.parse(response?.data?.data);
        if (result?.success) {
          this.globalStore.notificationStore.showSuccessNotification({
            message: 'Revision was successfully rejected.',
          });
          afterActionExecuted && afterActionExecuted();
        }
      }
      else {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'You have to give a reason for the rejection.',
        });
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Unable to complete the request, please contact the tech team.',
      });
    }
    this.setIsRejectDrawDialogOpen(false);
    this.setIsRejectRevisionDialogOpen(false);
    this.rejectReason = '';
  }

  *updateDrawStage(drawId, afterActionExecuted) {
    try {
      const response = yield this.drawActionsService.setDrawStage(drawId, this.newStage);
      const result = JSON.parse(response?.data?.data);
      if (result?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: "Draw stage was set to: " + this.newStage,
        });
        afterActionExecuted && afterActionExecuted();
      }
      else {
        this.globalStore.notificationStore.showErrorNotification({
          message: result?.responseMessage,
        });
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Unable to complete the request, please contact the tech team.',
      });
    }
    this.setIsSetDrawStageDialogOpen(false);
    this.newStage = '';
  }

  *getTransactionList(loanId, drawType) {
    try {
      this.transactionList = [];
      const response = yield this.drawActionsService.getTransactionGridByLoanId(loanId);
      const transactionType = drawType === DRAW_INSURANCE_TYPE ? INSURANCE_DRAW : REGULAR_DRAW;
      this.transactionList = response?.data?.data?.rows?.filter(item => item.transactionType === transactionType).map(item => {
        return { label: `${item.transactionId} - ${item.transactionType} - $${item.totalAmount}`, value: item.transactionId };
      }) ?? [];
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching the transaction list.',
      });
    }
  }

  *openLinkTransactionToDrawDialog(loanId, drawType) {
    yield this.getTransactionList(loanId, drawType);
    this.setIsLinkTransactionToDrawDialogOpen(true);
  }

  *linkTransactionId(drawId, afterActionExecuted) {
    try {
      const response = yield this.drawActionsService.linkTransactionIdToDraw(drawId, this.linkTransactionOption);
      const result = JSON.parse(response?.data?.data);
      if (result?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Transaction was successfully linked to draw.',
        });
        afterActionExecuted && afterActionExecuted();
      }
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Unable to link transaction, please contact the tech team.',
      });
    }
    this.setIsLinkTransactionToDrawDialogOpen(false);
    this.linkTransactionOption = null;
  }

  *getSnapDrawsEvents() {
    try {
      this.truepicEventList = [];
      const response = yield this.drawActionsService.getSnapDrawsEvents();
      const result = JSON.parse(response?.data?.data);
      const options = [];
      result?.map(event => {
        options.push(event.truepicId + " loan ID " + event.loanId + " draw # " + event.drawNumber);
      }) ?? [];
      this.truepicEventList = options;
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching the truepic events list.',
      });
    }
  }

  *getIfInspectionOpen(drawId) {
    try {
      this.inspectionOpen = false;
      const response = yield this.drawActionsService.getIfInspectionOpen(drawId);
      this.inspectionOpen = JSON.parse(response?.data?.data);
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while checking if the draw has an open snap draws request.',
      });
    }
  }

  *openMoveSnapDrawsPhotosDialog(drawId) {
    yield this.getSnapDrawsEvents();
    yield this.getIfInspectionOpen(drawId);
    this.setIsMoveSnapDrawsPhotosDialogOpen(true);
  }

  moveTruepicPhotos() {
    if (this.truepicEvent) {
      this.setIsTruepicConfirmDialogOpen(true);
    }
    else {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Please select a snap draw record to move to this draw.',
      });
    }
  }

  *movePictures(drawId, afterActionExecuted) {
    try {
      yield this.drawActionsService.moveTruepicPicturesToDraw(drawId, this.truepicEvent);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Photos were succesufully moved to this draw!',
      });
      afterActionExecuted && afterActionExecuted();
    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error has ocurred, please contact the tech support team.',
      });
    }
    this.setIsMoveSnapDrawsPhotosDialogOpen(false);
    this.truepicEvent = null;
    this.truepicOption = null;
    this.setIsTruepicConfirmDialogOpen(false);
  }

  setIsApproveRevisionDialogOpen(open: boolean) {
    this.isApproveRevisionDialogOpen = open;
  }

  setIsRejectRevisionDialogOpen(open: boolean) {
    this.isRejectRevisionDialogOpen = open;
  }

  *approveRevision(drawId: number, afterActionExecuted: () => void) {
    try {
      const response = yield this.drawActionsService.approveRevision(drawId);
      const result = JSON.parse(response?.data?.data);
      if (result?.success) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Revision was successfully approved.',
        });
        afterActionExecuted && afterActionExecuted();
      }

    } catch (err) {
      console.log(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Unable to complete the request, please contact the tech team.',
      });
    }
    this.setIsApproveRevisionDialogOpen(false);
  }

}
